import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { BannerService } from '@terminus-ui/banner';
import { ButtonComponent } from '@terminus-ui/button';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MeterGroupModule, MeterItem } from 'primeng/metergroup';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SliderModule } from 'primeng/slider';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  startWith,
  Subject,
  zip,
} from 'rxjs';
import { CampaignScoringService } from './campaign-scoring.service';
import { InputRangeComponent } from './components/input-range/input-range.component';
import { ScoringTypes, TimeDecayDuration } from './constants';

@Component({
  selector: 'lib-campaign-scoring',
  imports: [
    CommonModule,
    ButtonComponent,
    ReactiveFormsModule,
    MeterGroupModule,
    SliderModule,
    InputTextModule,
    InputNumberModule,
    RadioButtonModule,
    AccordionModule,
    CardModule,
    ButtonModule,
    CheckboxModule,
    InputRangeComponent,
  ],
  templateUrl: './campaign-scoring.component.html',
  styleUrl: './campaign-scoring.component.scss',
})
export class CampaignScoringComponent implements OnInit {
  private fb = inject(FormBuilder);
  private campaignScoringService = inject(CampaignScoringService);
  private destroyRef = inject(DestroyRef);
  private bannerService = inject(BannerService);

  public scoringTypes = ScoringTypes;

  public form = this.fb.group({
    scoringType: this.fb.control(ScoringTypes.percent),
    percent: this.fb.group({
      firstTouch: this.fb.control(40),
      lastTouch: this.fb.control(40),
      otherTouches: this.fb.control(20),
    }),
    customWeight: this.fb.group({
      channel: this.fb.group({}),
      touchPosition: this.fb.group({
        sourceTouch: this.fb.control(0),
        opptyConversionTouch: this.fb.control(0),
      }),
      timeDecay: this.fb.group({
        duration: this.fb.control(TimeDecayDuration.month),
        monthsOfHalfLife: this.fb.control(0),
      }),
      touchType: this.fb.group({
        opportunityContactRole: this.fb.control(5),
        accountContact: this.fb.control(0),
        orphanLead: this.fb.control(0),
        contactRoleOverride: this.fb.control(false),
      }),
      ocrRole: this.fb.group({}),
    }),
  });

  revenues$: Subject<string[]> = new Subject();
  roles$: BehaviorSubject<string[]> = new BehaviorSubject([] as string[]);

  isOutOfLimit$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  outOfLimitNotification = this.isOutOfLimit$
    .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
    .subscribe((outOfLimit) => {
      if (outOfLimit) {
        this.bannerService.pushBanner({
          type: 'error',
          title: 'Total percent must add up to 100%',
        });
      } else {
        this.bannerService.clearBanners();
      }
    });

  ngModels = {
    customWeight: {
      touchPosition: {
        sourceTouch: 0,
        opptyConversionTouch: 0,
      },
      touchType: {
        opportunityContactRole: 5,
        accountContact: 0,
        orphanLead: 0,
      },
      channel: {},
      ocrRole: {},
    },
  };

  customPropsFormMapping = {
    percent: {
      other: this.form.controls.percent.controls.otherTouches,
      source: this.form.controls.percent.controls.firstTouch,
      last: this.form.controls.percent.controls.lastTouch,
    },
    touchPosition: {
      'Source Touch':
        this.form.controls.customWeight.controls.touchPosition.controls
          .sourceTouch,
      'Oppty Conversion Touch':
        this.form.controls.customWeight.controls.touchPosition.controls
          .opptyConversionTouch,
    },
    touchType: {
      'Opportunity Contact Role':
        this.form.controls.customWeight.controls.touchType.controls
          .opportunityContactRole,
      'Account Contact':
        this.form.controls.customWeight.controls.touchType.controls
          .accountContact,
      'Orphan Lead':
        this.form.controls.customWeight.controls.touchType.controls.orphanLead,
    },
    time: {
      Months:
        this.form.controls.customWeight.controls.timeDecay.controls
          .monthsOfHalfLife,
      'Half Life':
        this.form.controls.customWeight.controls.timeDecay.controls.duration,
    },
    channel: {},
    ocrRole: {},
  };

  percentMeter$: Observable<{
    meter: MeterItem[];
    max: number;
  }> = this.form.controls.percent.valueChanges.pipe(
    startWith(this.form.controls.percent.value),
    map((value) => {
      const sum = +value.lastTouch! + +value.firstTouch! + +value.otherTouches!;
      const limit = 100;
      const meter: MeterItem[] = [
        {
          label: 'First Touch',
          color: '#34d399',
          value: value.firstTouch!,
          icon: 'pi pi-table',
        },
        {
          label: 'Last Touch',
          color: '#fbbf24',
          value: value.lastTouch!,
          icon: 'pi pi-inbox',
        },
        {
          label: 'Other Touches',
          color: '#60a5fa',
          value: value.otherTouches!,
          icon: 'pi pi-image',
        },
      ];
      if (sum > limit) {
        meter.push({
          label: 'Over Limit',
          color: '#da1a29',
          value: sum - 100,
          icon: 'pi pi-cog',
        });
      }
      if (sum < limit) {
        meter.push({
          label: 'Remaining',
          color: '#da1a29',
          value: 100 - sum,
          icon: 'pi pi-cog',
        });
      }

      this.isOutOfLimit$.next(sum !== limit);

      return {
        meter,
        max: sum > limit ? sum : limit,
      };
    }),
    takeUntilDestroyed(this.destroyRef),
  );

  public onOcrOverrideChange(ev: CheckboxChangeEvent) {
    if (ev.checked) {
      this.form.controls.customWeight.controls.touchType.controls.opportunityContactRole.setValue(
        0,
      );
      this.ngModels.customWeight.touchType.opportunityContactRole = 0;
    } else {
      this.form.controls.customWeight.controls.touchType.controls.opportunityContactRole.setValue(
        5,
      );
      this.ngModels.customWeight.touchType.opportunityContactRole = 5;
    }
  }

  public ngOnInit() {
    const requests = [
      this.campaignScoringService.getRevenueGroups().response$,
      this.campaignScoringService.loadOpportinityContactRoles().response$,
      this.campaignScoringService.loadConfig().response$,
      this.campaignScoringService.loadAttributionSelections().response$,
    ];

    zip(...requests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([revenues, roles, config, selections]) => {
        this.initRevenues(revenues);
        this.initOcrRoles(roles);
        this.initScoringType(config);
        this.prepopulateForm(selections);
      });
  }

  public save() {
    zip(
      ...[
        this.saveAttributionType(
          'weightType',
          ScoringTypes[this.form.value.scoringType!],
        ),
        this.form.value.scoringType === ScoringTypes.percent
          ? this.savePercent()
          : this.saveCustomWeight(),
      ],
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.markAsPristine();
      });
  }

  private initRevenues(revenues: string[]) {
    this.revenues$.next(revenues);
    revenues.forEach((revenue) => {
      this.form.controls.customWeight.controls.channel.addControl(
        revenue,
        this.fb.control(0),
      );
      this.ngModels.customWeight.channel[revenue] = 0;
      this.customPropsFormMapping.channel[revenue] =
        this.form.controls.customWeight.controls.channel.controls[revenue];
    });
  }

  private initOcrRoles(roles: string[]) {
    this.roles$.next(roles);
    roles.forEach((role) => {
      this.form.controls.customWeight.controls.ocrRole.addControl(
        role,
        this.fb.control(0),
      );
      this.ngModels.customWeight.ocrRole[role] = 0;
      this.customPropsFormMapping.ocrRole[role] =
        this.form.controls.customWeight.controls.ocrRole.controls[role];
    });
  }

  private initScoringType(config: {
    attribution: {
      custom: {
        weightType: string;
        ocrOverride: string;
      };
    };
  }) {
    this.form.controls.scoringType.setValue(
      config.attribution.custom
        ? ScoringTypes[config.attribution.custom.weightType]
        : ScoringTypes.percent,
    );
    this.form.controls.customWeight.controls.touchType.controls.contactRoleOverride.setValue(
      config.attribution.custom?.ocrOverride === 'true',
    );
  }

  private prepopulateForm(selections) {
    if (selections.percent) {
      selections.percent.forEach((i) => {
        this.customPropsFormMapping.percent[i.type].setValue(i.value);
      });
    } else {
      selections.touchPosition?.forEach((val) => {
        this.customPropsFormMapping.touchPosition[val.type].setValue(val.value);
        this.ngModels.customWeight.touchPosition[val.type] = val.value;
      });
      selections.touchType?.forEach((val) => {
        this.customPropsFormMapping.touchType[val.type].setValue(val.value);
        this.ngModels.customWeight.touchType[val.type] = val.value;
      });
      selections.channel?.forEach((val) => {
        this.customPropsFormMapping.channel[val.type].setValue(val.value);
        this.ngModels.customWeight.channel[val.type] = val.value;
      });
      selections.ocrRole?.forEach((val) => {
        this.customPropsFormMapping.ocrRole[val.type].setValue(val.value);
        this.ngModels.customWeight.ocrRole[val.type] = val.value;
      });
      selections.time?.forEach((val) => {
        this.customPropsFormMapping.time[val.type].setValue(val.value);
      });
    }
  }

  private savePercent() {
    const selections = [
      {
        object: 'percent',
        type: 'source',
        value: this.form.controls.percent.value.firstTouch,
      },
      {
        object: 'percent',
        type: 'last',
        value: this.form.controls.percent.value.lastTouch,
      },
      {
        object: 'percent',
        type: 'other',
        value: this.form.controls.percent.value.otherTouches,
      },
    ];

    return this.campaignScoringService.updateAttribution(selections);
  }
  private saveCustomWeight() {
    const payload = Object.entries(this.customPropsFormMapping).reduce<
      {
        object: string;
        type: string;
        value: any;
      }[]
    >((acc, [object, value]) => {
      return acc.concat(
        Object.entries(value).map(([fieldKey, fieldValue]) => ({
          object,
          type: fieldKey,
          value: fieldValue.value,
        })),
      );
    }, []);

    return zip(
      ...[
        this.campaignScoringService.updateAttribution(payload),
        this.saveAttributionType(
          'ocrOverride',
          this.form.value.customWeight?.touchType?.contactRoleOverride,
        ),
      ],
    );
  }

  private saveAttributionType(type: string, value: any) {
    const data = {
      attribution: {
        custom: {
          [type]: value,
        },
      },
    };
    return this.campaignScoringService.updateConfig(data).response$;
  }
}
